/**
 * PatientsArchiveToolbar — selection actions bar shown above the patient
 * list when 1+ rows are selected. Mirrors the Flutter row at
 * `patient_list_screen.dart:1459-1503` (hidden when the Archived chip is
 * on). Archive is a secondary button like the others (the approved design,
 * PatientsTable `.selectionBar`); the danger step is the confirm dialog.
 *
 * Now also surfaces the bulk **Merge** action (PROD-721) when ≥2 rows
 * are selected AND the caller passes `onMerge`. The Merge button is
 * non-destructive (default variant), as is Archive. Caller gates each independently via the `onArchive` / `onMerge`
 * presence (undefined ⇒ button hidden) so permissions stay at the
 * parent.
 *
 * Parent gates:
 *   1. Don't render at all when `count === 0`.
 *   2. Don't render Archive when the Archived chip is ON.
 *   3. Don't pass `onMerge` unless the user has
 *      `Manage_Patients_Demographics` + `edit: true`.
 *   4. Don't pass `onMerge` when fewer than 2 rows are selected.
 */

import { Button } from '@scripsteam/scrips-signal-ds';

export interface PatientsArchiveToolbarProps {
  count: number;
  onClear: () => void;
  /** Bulk archive action. Omit to hide the Archive button. */
  onArchive?: () => void;
  /** Bulk merge action. Omit to hide the Merge button. Parent must
   *  ensure `count >= 2` before passing this. */
  onMerge?: () => void;
  /** Bulk cohort enrolment (PROD-3206). Omit to hide the button; the parent passes it only
   *  when `isCohortEnrolmentOn()` is on (deploy flag + `?x=cohorts`). */
  onEnrol?: () => void;
  /** Disables every button while a mutation is in flight. */
  isPending?: boolean;
}

export function PatientsArchiveToolbar({
  count,
  onClear,
  onArchive,
  onMerge,
  onEnrol,
  isPending = false,
}: PatientsArchiveToolbarProps) {
  if (count === 0) return null;
  return (
    // Look from the bridge (design PatientsTable `.selectionBar`): the active-blue tint on a brand hairline, the count
    // in body strong and Clear in body, both in the active-blue ink; every action a secondary button.
    <div data-slot="patients-selection-bar">
      <div data-slot="patients-selection-lead">
        <span data-slot="patients-selection-count">
          {count} selected
        </span>
        <button
          type="button"
          onClick={onClear}
          disabled={isPending}
          data-slot="patients-selection-clear"
        >
          Clear
        </button>
      </div>
      <div data-slot="patients-selection-actions">
        {onEnrol ? (
          <Button type="button" variant="secondary" size="sm" onClick={onEnrol} disabled={isPending}>
            Enrol in cohort
          </Button>
        ) : null}
        {onMerge ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onMerge}
            disabled={isPending}
          >
            {/* Plain text as the design (PatientsTable `.selectionActions`): no glyph, no count. */}
            Merge
          </Button>
        ) : null}
        {onArchive ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onArchive}
            disabled={isPending}
          >
            {isPending ? 'Archiving…' : 'Archive'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
